import {
  IProvidedTranslationProps,
  withTranslations,
} from '@wix/wixstores-client-common-components/dist/es/src/outOfIframes/translations';
import classNames from 'classnames';
import React, {useEffect, useRef} from 'react';
import {BoxSelection, BoxSelectionBoxSize} from 'wix-ui-tpa/cssVars';
import {ProductOptionWidthType, UserInputType} from '../../../constants';
import {useGlobals, withGlobalProps} from '../../../providers/globalPropsProvider';
import {useStylesParams} from '../../../providers/stylesParamsProvider';
import {scrollAndFocusUserInput} from '../utils/scrollAndFocusUserInput';
import s from './OptionsButtons.scss';
import {classes as boxSelectionStyles} from './OptionsButtons.st.css';

export interface OptionsButtonsProps extends IProvidedTranslationProps {
  optionIndex: number;
}

export enum OptionsButtonsDataHook {
  Container = 'options-buttons-container',
  Buttons = 'options-buttons',
  Title = 'options-buttons-title',
}
// eslint-disable-next-line sonarjs/cognitive-complexity
const OptionsButtonsComponent: React.FC<OptionsButtonsProps> = ({optionIndex, t}) => {
  const globals = useGlobals();
  const buttonContainerRef = useRef<HTMLDivElement>(null);
  const stylesParams = useStylesParams();

  const {
    product,
    userInputs,
    variantInfo,
    isBackInStockEnabled,
    handleUserInput,
    validateUserInputsChanges,
    scrollAndFocusInputTarget,
    isQuickView,
    isProductSubmitted,
    userInputErrors,
    styles,
  } = globals;
  const fitToText = styles.get(stylesParams.productPage_buttonOptionWidthType) === ProductOptionWidthType.FIT;

  useEffect(() => {
    if (!scrollAndFocusInputTarget || !buttonContainerRef.current) {
      return;
    }

    const isScrollAndFocusInputTarget =
      UserInputType.Selection === scrollAndFocusInputTarget?.inputType &&
      optionIndex === scrollAndFocusInputTarget?.index;
    /* istanbul ignore else */
    if (isScrollAndFocusInputTarget) {
      scrollAndFocusUserInput(UserInputType.Selection, buttonContainerRef.current, isQuickView);
    }
  }, [scrollAndFocusInputTarget, optionIndex, isQuickView]);

  const productOption = product?.options?.[optionIndex];

  const handleChange = ({id: selectionId}: {id: string}) => {
    const nextSelection = productOption.selections.find(({id}) => id.toString() === selectionId);

    handleUserInput(UserInputType.Selection, nextSelection, optionIndex);
    validateUserInputsChanges();
  };

  const isSelectionAvailable = (isSelectable: boolean): boolean => {
    return isBackInStockEnabled ? !isSelectable : false;
  };

  const activeSelection = userInputs?.[UserInputType.Selection]?.[optionIndex];

  const getSelectionAvailabilityInfo = (selectionId: number) => {
    return variantInfo.selectionsAvailability[productOption.id][selectionId];
  };

  const getVisibleSelections = () => {
    return productOption.selections
      .filter((selection) => getSelectionAvailabilityInfo(selection.id).isVisible)
      .map((selection) => {
        const availabilityInfo = getSelectionAvailabilityInfo(selection.id);
        return {
          ...selection,
          isSelectable: isBackInStockEnabled ? true : availabilityInfo.isSelectable,
          unavailable: isSelectionAvailable(availabilityInfo.isSelectable),
        };
      });
  };

  const {title} = productOption;
  const hasError = isProductSubmitted && userInputErrors?.[UserInputType.Selection]?.[optionIndex];
  return (
    <div data-hook={OptionsButtonsDataHook.Container} ref={buttonContainerRef}>
      <BoxSelection
        required
        aria-required
        label={title}
        name={`options-buttons-${optionIndex}`}
        aria-label={title}
        size={BoxSelectionBoxSize.xSmall}
        onChange={handleChange}
        className={classNames(boxSelectionStyles.optionItem, {
          [s.fitToText]: fitToText,
        })}
        data-hook={OptionsButtonsDataHook.Buttons}
        error={hasError}
        errorMessage={t('SELECT_OPTION_WARNING', {optionTitle: title})}>
        {getVisibleSelections().map((selection) => (
          <BoxSelection.Option
            key={selection.id}
            id={selection.id.toString()}
            unstableHasInnerWrapper={false}
            checked={activeSelection?.id === selection.id}
            aria-label={selection.description}
            disabled={selection.unavailable}
            data-hook={`box-selection-option-${selection.id}`}
            className={classNames(boxSelectionStyles.optionItem, {
              [s.flexGrow]: fitToText,
            })}>
            <div
              data-hook={`box-selection-text-option-${selection.id}`}
              className={classNames(s.optionItemText, {
                [s.strikethrough]: selection.unavailable,
              })}>
              {selection.description}
            </div>
          </BoxSelection.Option>
        ))}
      </BoxSelection>
    </div>
  );
};

export const OptionsButtons: React.FC<OptionsButtonsProps> = withGlobalProps(
  withTranslations('globals.texts')(OptionsButtonsComponent)
);
